import clsx from 'clsx';
import _ from 'lodash';
import { HoverTooltip } from '@/core/HoverTooltip.atom';
import { PUSH_IGNORE } from '@/core/flux.service';
import { useAllTrendStoreItems } from '@/core/hooks/useAllTrendStoreItems.hook';
import { addTrendItem, fetchItemDependencies, removeItems } from '@/trendData/trend.actions';
import { sqItemsApi } from '@seeqdev/api/endpoint/ItemsApi';
import type { TooltipPosition } from '@seeqdev/qomponents';

export interface AddToDisplayPaneIF {
  itemId: string | string[];
  tooltip?: string;
  removeTooltip?: string;
  extraClassNames?: string;
  tooltipPlacement?: TooltipPosition;
  afterAdd?: (itemId: string) => Promise<void> | void;
  outputItemId?: string;
  additionalProps?: Record<string, unknown> | Record<string, unknown>[];
  allowRemove?: boolean;
  afterRemove?: (itemId: string) => Promise<void> | void;
}

export const AddToDisplayPane = (props: AddToDisplayPaneIF) => {
  const {
    itemId,
    tooltip,
    removeTooltip: removeTooltipProp,
    extraClassNames,
    tooltipPlacement,
    afterAdd,
    outputItemId,
    additionalProps,
    allowRemove,
    afterRemove,
  } = props;

  const itemIds = Array.isArray(itemId) ? itemId : [itemId];
  const isMultiple = itemIds.length > 1;

  const trendItems = useAllTrendStoreItems();

  const trendItemIds = new Set(trendItems?.map((item) => item.id));

  const outputItem = outputItemId ? _.find(trendItems, { id: outputItemId }) : undefined;
  const outputItemProps = outputItem
    ? { tableId: _.get(outputItem, 'tableId'), rowId: _.get(outputItem, 'rowId') }
    : undefined;

  const missingItems = itemIds
    .map((id, index) => {
      const perItemProps = Array.isArray(additionalProps) ? additionalProps[index] : additionalProps;
      const itemProps =
        outputItemProps && perItemProps ? { ...outputItemProps, ...perItemProps } : (outputItemProps ?? perItemProps);
      return { id, itemProps };
    })
    .filter(({ id }) => !trendItemIds.has(id));

  const presentIds = itemIds.filter((id) => trendItemIds.has(id));

  const addOrRemoveNotNeeded = missingItems.length === 0 && (!allowRemove || presentIds.length === 0);
  if (addOrRemoveNotNeeded) {
    return null;
  }

  const prepareItemForAdd = async ({
    id,
    itemProps,
  }: {
    id: string;
    itemProps: Record<string, unknown> | undefined;
  }) => {
    const { data: item } = await sqItemsApi.getItemAndAllProperties({ id });
    return { enrichedItem: { ...item, ...itemProps }, itemProps };
  };

  const addToDisplay = async () => {
    if (missingItems.length === 0) return;

    // For single items, use normal addTrendItem behavior
    if (missingItems.length === 1) {
      const { enrichedItem, itemProps } = await prepareItemForAdd(missingItems[0]);
      await addTrendItem(enrichedItem, itemProps);

      if (afterAdd) {
        await afterAdd(missingItems[0].id);
      }
      return;
    }

    // For multiple items, batch the adds to avoid N calls to fetchItemDependencies
    await Promise.all(
      missingItems.map(async (item) => {
        const { enrichedItem, itemProps } = await prepareItemForAdd(item);
        // PUSH_IGNORE avoids N workstep entries, skipDependencies=true avoids N dependency fetches
        return addTrendItem(enrichedItem, itemProps, PUSH_IGNORE, true);
      }),
    );

    fetchItemDependencies();

    if (afterAdd) {
      await Promise.all(missingItems.map(({ id }) => afterAdd(id)));
    }
  };

  const removeFromDisplay = async () => {
    await removeItems(presentIds.map((id) => ({ id })));

    if (afterRemove) {
      await Promise.all(presentIds.map((id) => afterRemove(id)));
    }
  };

  const addTooltip = tooltip || (isMultiple ? 'ADD_ALL_TO_DISPLAY' : 'ADD_TO_DISPLAY');
  const removeTooltip = removeTooltipProp || (isMultiple ? 'REMOVE_ALL_FROM_DISPLAY' : 'REMOVE_FROM_DISPLAY');

  return (
    <>
      {allowRemove && presentIds.length > 0 && (
        <HoverTooltip placement={tooltipPlacement || 'right'} text={removeTooltip}>
          <a
            data-testid="removeFromDisplay"
            className={clsx(
              'displayIcon a fa-sharp fa-regular fa-times-circle link-no-underline animated animate-if-opacity pt2 pl5' +
                ' force-link-color',
              extraClassNames,
            )}
            onClick={removeFromDisplay}
          />
        </HoverTooltip>
      )}
      {missingItems.length > 0 && (
        <HoverTooltip placement={tooltipPlacement || 'right'} text={addTooltip}>
          <a
            data-testid="addToDisplay"
            className={clsx(
              'displayIcon a fa-sharp fa-regular fa-chart-area link-no-underline animated animate-if-opacity pt2 pl5' +
                ' force-link-color',
              extraClassNames,
            )}
            onClick={addToDisplay}
          />
        </HoverTooltip>
      )}
    </>
  );
};
